/**
 * GRADING MODELS REST client (group E). Models are READ
 * local-first (sync `academy_org`), but WRITTEN via REST (admin config;
 * owner/org_admin ∥ platform admin authz in the handler). Same cookie-BFF
 * auth pattern as the rest of the server-only clients (metrics/CRM/billing).
 */
import type { GradingLetterBand, GradingModelKind } from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/academy";

/** Create/edit body (0..1 fraction for the threshold). */
export interface GradingModelInput {
  name?: string;
  kind?: GradingModelKind;
  max_value?: number;
  passing_threshold?: number;
  decimals?: number;
  letter_bands?: GradingLetterBand[] | null;
  display_suffix?: string | null;
  clone_from?: string;
}

/**
 * The SERVER's verdict on what can be done with each model.
 *
 * It isn't recalculated here: "having grades" is a database fact (live
 * grades, sealed final grades and issued certificates) that the client doesn't see
 * via sync. The interface only REFLECTS this; what actually blocks the operation is the
 * PATCH/DELETE, which returns 409 even if someone renders the button by hand.
 */
export interface GradingModelUsage {
  id: string;
  /** Chosen by the organization, a course or a cohort. */
  in_use: boolean;
  /** Sostiene notas ya puestas ⇒ congelado. */
  has_grades: boolean;
  editable: boolean;
  deletable: boolean;
}

function orgUrl(orgId: string): string {
  return `${BASE}/${encodeURIComponent(orgId)}/grading-models`;
}

/**
 * The academy's models usage status, by id. The models LIST
 * still arrives via sync (local-first); this is only the derived verdict.
 */
export async function fetchGradingModelUsage(
  orgId: string,
): Promise<Record<string, GradingModelUsage>> {
  const rows = await apiJson<GradingModelUsage[]>(orgUrl(orgId));
  const byId: Record<string, GradingModelUsage> = {};
  for (const row of rows) {
    byId[row.id] = row;
  }
  return byId;
}

export function createGradingModel(orgId: string, input: GradingModelInput) {
  return apiJson<unknown>(orgUrl(orgId), { method: "POST", body: JSON.stringify(input) });
}

export function updateGradingModel(orgId: string, modelId: string, input: GradingModelInput) {
  return apiJson<unknown>(`${orgUrl(orgId)}/${encodeURIComponent(modelId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function deleteGradingModel(orgId: string, modelId: string): Promise<void> {
  await apiJson<unknown>(`${orgUrl(orgId)}/${encodeURIComponent(modelId)}`, { method: "DELETE" });
}
